import { Component, Input, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { InstantCheckupService } from '@services/instant-checkup-service';
import { FilePickerModule } from '@components/file-picker';
import { DataStoreService } from '@services/data-store-service';
import { AppConfig } from '../../app.config';
import { FilePickerComponent } from '@components/file-picker/file-picker.component';
import { NgClass } from '@angular/common';
import { EventLoggerService } from '@services/event-logger-service';
import { LocalStorageService } from '@services/local-storage-service';
import { SelfCheckupService } from '@services/self-checkup-service';
import { FollowUp, User } from '@cureskin/api-client/src/server-api';

@Component({
  selector: 'app-self-checkup-banner',
  standalone: true,
  imports: [
    FilePickerModule,
    NgClass
  ],
  templateUrl: './self-checkup-banner.component.html',
})
export class SelfCheckupBannerComponent {
  @ViewChild(FilePickerComponent, { static: false }) filePickerComponent: FilePickerComponent;
  @Input() user: User;
  @Input() instantCheckups: any;
  @Input() followUp: FollowUp;
  @Input() shouldShowUpdatedUI: boolean = false;
  @Input() daysSinceLastDelivery: number | null = null;
  userInitialImage: any;
  username: string;
  nativeCameraImage: any;
  queryParams:{ [key: string]: any } = { tag: this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE }

  constructor(private readonly router: Router,
    private readonly instantCheckupService: InstantCheckupService,
    private readonly dataStore: DataStoreService,
    private readonly eventLoggerService: EventLoggerService,
    private readonly localStorageService: LocalStorageService,
    private readonly selfCheckupService: SelfCheckupService,
    protected appConfig: AppConfig) {
  }

  async ngOnInit(): Promise<void> {
    this.userInitialImage = [...this.instantCheckups].sort((a, b): number =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())[0]?.imagePath;
    this.username = this.user?.get('PatientName')?.split(' ')[0];
    this.createTag();
  }

  createTag(): void {
    const { InstantCheckup } = this.appConfig.Shared;
    const fiveDaysAgo = new Date(Date.now() - 5 * 24 * 60 * 60 * 1000);

    const recent = this.instantCheckups.filter((ic: { createdAt: string | number | Date; type: any; }) => {
      const created = new Date(ic.createdAt);
      return created >= fiveDaysAgo && [InstantCheckup.Type.FRONT_FACE, InstantCheckup.Type.SIDE_FACE].includes(ic.type);
    });

    const userFrontImage = recent.find((ic: { type: string; }): boolean => ic.type === InstantCheckup.Type.FRONT_FACE)?.imagePath;
    const userSideImage = recent.find((ic: { type: string; }): boolean => ic.type === InstantCheckup.Type.SIDE_FACE)?.imagePath;

    if (userFrontImage) {
      this.queryParams = { tag: this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE }
    } else if (userSideImage) {
      this.queryParams = { tag: this.appConfig.Shared.InstantCheckup.Type.SIDE_FACE }
    }
  }

  async bannerClick(): Promise<void> {
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({ name: 'self_checkup_photo_banner' }));
    this.queryParams = { ...this.queryParams, fromSelfCheckupBanner: true };
    this.localStorageService.setValue('fromSelfCheckupBanner', JSON.stringify(true));

    if (this.shouldShowUpdatedUI) {
      await this.selfCheckupService.navigateToFollowUpChatFromBanner(this.followUp);
      return;
    }

    const handledByNative = this.instantCheckupService.startCapture(this.queryParams, this.queryParams.tag);
    if (!handledByNative) {
      this.filePickerComponent.openFileIntent();
    }
  }

  async uploadImageFromNativeCamera(event: any): Promise<any> {
    this.nativeCameraImage = event.target.files[0];
    await this.dataStore.set('IMAGE_FILE', { file: this.nativeCameraImage });
    await this.router.navigate(['/user/instantCheckup/capture'],
      { queryParams: { ...this.queryParams, nativeCameraFile: true, fromSelfCaptureBanner: true }, replaceUrl: true });
  }
}
